import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth, isSuperAdmin } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { getCommerceId } from '@/lib/ownership';
import { getOrganizationByCommerce } from '@/lib/zifekCore';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — Résolution des permissions de l'utilisateur connecté
// chemin : user connecté → idcommerce → organisation → membership
//          → permissions (colonne jsonb ou rôle via core_role_permissions)
// Le propriétaire (membership is_primary / role owner) obtient « * ».
// Le superadmin obtient toujours l'accès complet.
// ─────────────────────────────────────────────────────────────

export interface PermissionSnapshot {
  permissions: string[];
  isOwner: boolean;
  roleKey: string;
  roleLabel: string;
}

// Cache mémoire : une seule résolution par utilisateur (partagée entre
// le layout et le garde d'accès, sans re-requête).
const cache = new Map<number, PermissionSnapshot>();
const inflight = new Map<number, Promise<PermissionSnapshot>>();

const FULL: PermissionSnapshot = { permissions: ['*'], isOwner: true, roleKey: 'owner', roleLabel: 'Propriétaire' };

async function resolvePermissions(userId: number, commerceId: number): Promise<PermissionSnapshot> {
  try {
    const org = await getOrganizationByCommerce(commerceId);
    // Aucune organisation trouvée : on ne bloque pas (superadmin / legacy).
    if (!org) return FULL;

    const { data: membership } = await supabase
      .from('core_memberships')
      .select('role, role_id, status, is_primary, permissions')
      .eq('organization_id', org.id)
      .eq('user_id', userId)
      .maybeSingle();

    // Pas de membership : propriétaire historique / superadmin → accès complet.
    if (!membership) return FULL;

    if (membership.is_primary || membership.role === 'owner') return FULL;

    const roleKey = (membership.role as string) || 'member';
    const roleLabel = (membership.role_label as string) || roleKey;

    let perms: string[] = Array.isArray(membership.permissions)
      ? (membership.permissions as string[])
      : [];

    if (perms.length === 0 && membership.role_id) {
      const { data: rolePerms } = await supabase
        .from('core_role_permissions')
        .select('permission')
        .eq('role_id', membership.role_id);
      perms = (rolePerms || []).map((r) => (r as { permission: string }).permission);
    }

    return { permissions: perms, isOwner: false, roleKey, roleLabel };
  } catch {
    // En cas d'erreur (réseau), on n'enferme pas l'utilisateur dehors.
    return FULL;
  }
}

function loadPermissions(userId: number, commerceId: number): Promise<PermissionSnapshot> {
  const cached = cache.get(userId);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(userId);
  if (pending) return pending;

  const promise = resolvePermissions(userId, commerceId)
    .then((snapshot) => {
      cache.set(userId, snapshot);
      inflight.delete(userId);
      return snapshot;
    })
    .catch(() => {
      inflight.delete(userId);
      return FULL;
    });
  inflight.set(userId, promise);
  return promise;
}

/** Vide le cache (utile après un changement de rôle côté marchand). */
export function invalidatePermissions(userId?: number): void {
  if (userId == null) cache.clear();
  else cache.delete(userId);
}

export interface PermissionsApi {
  loading: boolean;
  ready: boolean;
  isOwner: boolean;
  permissions: string[];
  /** Clé normalisée du rôle du membre connecté (ex : manager). */
  roleKey: string;
  /** Libellé lisible du rôle (ex : Manager). */
  roleLabel: string;
  /** Vrai si l'utilisateur possède la permission (ou au moins une si liste). */
  has: (permission: string | string[] | null | undefined) => boolean;
}

export function usePermissions(): PermissionsApi {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const commerceId = useMemo(() => getCommerceId(user), [user]);

  const [snapshot, setSnapshot] = useState<PermissionSnapshot | null>(
    userId != null ? cache.get(userId) ?? null : null,
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userId == null || commerceId == null) {
      setSnapshot({ permissions: [], isOwner: false, roleKey: '', roleLabel: '' });
      return undefined;
    }
    const cached = cache.get(userId);
    if (cached) {
      setSnapshot(cached);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    loadPermissions(userId, commerceId).then((snap) => {
      if (cancelled) return;
      setSnapshot(snap);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId, commerceId]);

  const permissions = snapshot?.permissions ?? [];
  const typecompte = user?.typecompte;

  const has = useCallback(
    (permission: string | string[] | null | undefined): boolean => {
      if (permission == null) return true;
      // Tant que les permissions ne sont pas résolues, on ne masque rien
      // (évite un flash de menu vide au premier chargement).
      if (snapshot === null) return true;
      if (typecompte != null && isSuperAdmin(typecompte)) return true;
      if (permissions.includes('*')) return true;
      const needed = Array.isArray(permission) ? permission : [permission];
      if (needed.length === 0) return true;
      return needed.some((p) => permissions.includes(p));
    },
    [permissions, typecompte, snapshot],
  );

  return {
    loading,
    ready: snapshot !== null,
    isOwner: snapshot?.isOwner ?? false,
    permissions,
    roleKey: snapshot?.roleKey ?? '',
    roleLabel: snapshot?.roleLabel ?? '',
    has,
  };
}

export default usePermissions;